import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { Router } from '@angular/router';
import { FilterModel } from 'ag-grid-enterprise';
import { DateTime } from 'luxon';
import { Observable, Subscription, forkJoin, from, map, of } from 'rxjs';
import { CompanyVendor, User } from 'src/app/classes/models';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';
import { UtilityService } from 'src/app/services/utility.service';
import { companyVendorsService } from 'src/app/shared/vive-api/company-vendors.service';
import { CompanyVendorsFindAllByCompanyIdRequestDtoViveStatusesEnum } from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

@Component({
  selector: 'app-alerts-metrics-widget',
  templateUrl: './alerts-metrics-widget.component.html',
  styleUrls: ['./alerts-metrics-widget.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AlertsMetricsWidgetComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private CompanyVendorService: CompanyVendorService,
    private ToastService: ToastService,
    private Router: Router,
    private UtilityService: UtilityService,
    private UserService: UserService,
    private AuthService: AuthService,
    private SharedDataService: SharedDataService
  ) {}

  @Input() CompanyId!: number;

  alertDataMap: Map<
    string,
    {
      count: number;
      query?: {} | undefined;
      route?: string | undefined;
      FilterModel?: FilterModel;
    }
  > = new Map();

  usersNotLoggedInLast30Days?: { count: number; FilterModel: FilterModel };
  usersLoggedInLast30Days?: { count: number; FilterModel: FilterModel };

  userAccountRole?: UserAccountRoles;
  SharedDataServiceSubscription?: Subscription;

  getWidgetTitle() {
    if (this.userAccountRole) {
      if (
        !['CLIENT_ADMIN', 'CLIENT_VENDOR_AND_SITES_MANAGER'].includes(
          this.userAccountRole
        )
      ) {
        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      }

      const RoleToTitleMap = {
        CLIENT_ADMIN: 'Status Alerts and User Metrics',
        CLIENT_VENDOR_AND_SITES_MANAGER: 'Status Alerts'
      };

      return RoleToTitleMap[
        this.userAccountRole as
          'CLIENT_ADMIN' | 'CLIENT_VENDOR_AND_SITES_MANAGER'
      ];
    }

    return '';
  }

  onClickNavigateToUsersGrid(FilterModel: FilterModel, title: string) {
    this.Router.navigateByUrl(
      `client/${this.AuthService.getUserCompanyId()}/admin/users`,
      { state: { FilterModel, title } }
    );
  }

  isAViveStatus(status: string): boolean {
    return ['APPROVED', 'SUSPENDED', 'REGISTERED', 'Inactive'].includes(status);
  }

  keyValuePairDefaultOrder(
    a: KeyValue<any, any>,
    b: KeyValue<any, any>
  ): number {
    return 0;
  }

  isObjectWithCount(value: any): value is { count: number; query: {} } {
    return value && typeof value === 'object' && 'count' in value;
  }

  navigateToGrid(displayCount: { [key: string]: any }) {
    const { query, route, FilterModel } = displayCount['value'];

    if (route && FilterModel) {
      return this.Router.navigateByUrl(route, {
        state: {
          title: displayCount['key'].concat(' last 30 days'),
          FilterModel
        }
      });
    }

    if (route && query) {
      return this.Router.navigateByUrl(route, {
        state: { query, title: displayCount['key'].concat(' last 30 days') }
      });
    }

    if (route) {
      return this.Router.navigateByUrl(route);
    }

    return;
  }

  loadData() {
    const currentDate = new Date();
    const thirtyDaysAgo = new Date().setDate(currentDate.getDate() - 30);
    const clientVendorRoute = `client/${this.CompanyId}/vendors/client`;

    const usersNotLoggedInLast30DaysQuery = {
      lastLoginDate: { $lt: thirtyDaysAgo }
    };

    const usersLoggedInLast30DaysQuery = {
      lastLoginDate: { $gte: thirtyDaysAgo }
    };

    // A counted page of one row: the API counts in SQL instead of building
    // and sending every relationship. Inactive relationships are left out of
    // the client view unless asked for.
    const getStatusCount = (
      viveStatus: CompanyVendorsFindAllByCompanyIdRequestDtoViveStatusesEnum
    ): Observable<number> =>
      from(
        companyVendorsService.findAllByCompanyId({
          options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS },
          pathParams: { companyId: this.CompanyId },
          queryParams: {
            viveStatuses: [viveStatus],
            statusDateStart: new Date(thirtyDaysAgo).toISOString(),
            statusDateEnd: currentDate.toISOString(),
            pageSize: 1,
            includeCount: true,
            ...(viveStatus === 'Inactive' && { includeInactive: true })
          }
        })
      ).pipe(map(({ meta }) => meta.total ?? 0));

    const getCompanyVendorsFilterModel = (
      viveStatus: 'APPROVED' | 'SUSPENDED' | 'REGISTERED' | 'Inactive'
    ) => {
      return {
        viveStatus: {
          type: 'set',
          values: [viveStatus]
        },
        statusDate: {
          filterType: 'date',
          type: 'inRange',
          dateFrom: DateTime.fromJSDate(new Date())
            .minus({ months: 1 })
            .toFormat('yyyy-MM-dd HH:mm:ss'),
          dateTo: DateTime.fromJSDate(new Date()).toFormat(
            'yyyy-MM-dd HH:mm:ss'
          )
        }
      };
    };

    const getRecentVendorFieldUpdatesQuery = (
      vendorType: 'Vendor' | 'ClientRegisteredVendor' | 'ExemptVendor'
    ) => {
      return {
        attributes: ['id'],
        [vendorType]: {
          options: { required: true },
          attributes: ['id'],
          ChangeHistory: {
            attributes: [],
            options: {
              required: true
            },
            ChangeHistoryField: {
              attributes: [],
              options: {
                required: true
              },
              updatedAt: {
                $and: [{ $gte: thirtyDaysAgo }, { $lte: currentDate }]
              }
            }
          }
        }
      };
    };

    if (this.userAccountRole === 'CLIENT_ADMIN') {
      forkJoin([
        this.UserService.getUserCountV2(
          this.UtilityService.stringifyObjectsInQueryObject(
            usersNotLoggedInLast30DaysQuery
          )
        ),
        this.UserService.getUserCountV2(
          this.UtilityService.stringifyObjectsInQueryObject(
            usersLoggedInLast30DaysQuery
          )
        )
      ]).subscribe({
        next: ([usersNotLoggedInLast30Days, usersLoggedInLast30Days]) => {
          this.usersNotLoggedInLast30Days = {
            count: usersNotLoggedInLast30Days,
            FilterModel: {
              lastLoginDate: {
                filterType: 'date',
                type: 'lessThan',
                dateFrom: DateTime.fromJSDate(new Date())
                  .minus({ months: 1 })
                  .toFormat('yyyy-MM-dd HH:mm:ss'),
                dateTo: null
              }
            }
          };
          this.usersLoggedInLast30Days = {
            count: usersLoggedInLast30Days,
            FilterModel: {
              lastLoginDate: {
                filterType: 'date',
                type: 'greaterThan',
                dateFrom: DateTime.fromJSDate(new Date())
                  .minus({ months: 1 })
                  .toFormat('yyyy-MM-dd HH:mm:ss'),
                dateTo: null
              }
            }
          };
          this._cdr.markForCheck();
        }
      });
    }

    // Only a client admin sees the Vendor Field Updates row, so only a client
    // admin pays for its queries.
    const recentVendorFieldUpdates: Observable<CompanyVendor[][] | null> =
      this.userAccountRole === 'CLIENT_ADMIN'
        ? forkJoin([
            this.CompanyVendorService.getCompanyVendors(
              this.UtilityService.stringifyObjectsInQueryObject(
                getRecentVendorFieldUpdatesQuery('Vendor')
              )
            ),
            this.CompanyVendorService.getCompanyVendors(
              this.UtilityService.stringifyObjectsInQueryObject(
                getRecentVendorFieldUpdatesQuery('ClientRegisteredVendor')
              )
            ),
            this.CompanyVendorService.getCompanyVendors(
              this.UtilityService.stringifyObjectsInQueryObject(
                getRecentVendorFieldUpdatesQuery('ExemptVendor')
              )
            )
          ])
        : of(null);

    forkJoin([
      getStatusCount('Approved'),
      getStatusCount('Suspended'),
      getStatusCount('Registered'),
      getStatusCount('Inactive'),
      recentVendorFieldUpdates
    ]).subscribe({
      next: ([
        approvedCount,
        suspendedCount,
        registeredCount,
        inactivatedCount,
        recentlyUpdated
      ]) => {
        this.alertDataMap.set('Approved', {
          count: approvedCount,
          route: clientVendorRoute,
          FilterModel: getCompanyVendorsFilterModel('APPROVED')
        });
        this.alertDataMap.set('Suspended', {
          count: suspendedCount,
          route: clientVendorRoute,
          FilterModel: getCompanyVendorsFilterModel('SUSPENDED')
        });
        this.alertDataMap.set('Registered', {
          count: registeredCount,
          route: clientVendorRoute,
          FilterModel: getCompanyVendorsFilterModel('REGISTERED')
        });
        this.alertDataMap.set('Inactivated', {
          count: inactivatedCount,
          route: clientVendorRoute,
          FilterModel: getCompanyVendorsFilterModel('Inactive')
        });

        if (recentlyUpdated) {
          const [
            recentlyUpdatedVendors,
            recentlyUpdatedClientRegisteredVendors,
            recentlyUpdatedExemptVendors
          ] = recentlyUpdated;
          const mergedUpdatedVendors = [
            ...recentlyUpdatedVendors,
            ...recentlyUpdatedClientRegisteredVendors,
            ...recentlyUpdatedExemptVendors
          ];
          const mergedUpdatedVendorsQuery = {
            id: { $in: mergedUpdatedVendors.map((v) => v.id) }
          };
          this.alertDataMap.set('Vendor Field Updates', {
            count: mergedUpdatedVendors.length,
            route: clientVendorRoute,
            query: mergedUpdatedVendorsQuery
          });
        }
        this._cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.SharedDataServiceSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    this.SharedDataServiceSubscription =
      this.SharedDataService.listenForEvent<User>().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'SetLoggedInUser' && data) {
            if (!data) {
              throw this.ToastService.addErrorToast(
                'Error',
                'There was an error retrieving user information.',
                { returnError: true }
              );
            }

            const { UserAccountRole } = data;

            if (!UserAccountRole) {
              throw this.ToastService.addErrorToast(
                'Error',
                'User is missing permission information.',
                { returnError: true }
              );
            }

            const { role } = UserAccountRole;

            if (!role) {
              throw this.ToastService.addErrorToast(
                'Error',
                'User is missing permission.',
                { returnError: true }
              );
            }

            // The dashboard answers every widget that asks for the user, so
            // the same user arrives once per asking widget. Load on the first.
            if (this.userAccountRole) {
              return;
            }

            this.userAccountRole = role;

            this.loadData();
            this._cdr.markForCheck();
          }
        }
      });

    this.SharedDataService.sendEvent('GetLoggedInUser');
  }
}
